import { useState, useEffect, useRef } from "react";
import { useFirestoreRealtime } from "./useFirestoreRealtime";

interface UseNewIssuesNotificationOptions {
  collectionName: string;
  thresholdMinutes?: number; // How recent an issue should be to trigger notification
  enableNotifications?: boolean;
  notificationTitle?: string;
}

interface NewIssueNotification {
  id: string;
  message: string;
  timestamp: Date;
  data: any;
}

export function useNewIssuesNotification({
  collectionName,
  thresholdMinutes = 5,
  enableNotifications = true,
  notificationTitle = "New Issue Detected",
}: UseNewIssuesNotificationOptions) {
  const [newIssues, setNewIssues] = useState<NewIssueNotification[]>([]);
  const [hasNewIssues, setHasNewIssues] = useState(false);
  const previousDataRef = useRef<any[]>([]);
  const notificationTimeoutRef = useRef<NodeJS.Timeout>();

  const { data, lastUpdated } = useFirestoreRealtime({
    collectionName,
    orderByField: "timestamp",
    orderDirection: "desc",
    limitCount: 100,
  });

  // Check for new issues when data changes
  useEffect(() => {
    if (!data || data.length === 0) return;

    const now = new Date();
    const thresholdMs = thresholdMinutes * 60 * 1000;

    // Find new issues that were added recently
    const recentIssues = data.filter((item) => {
      const itemTimestamp =
        item.timestamp?.toDate?.() || new Date(item.timestamp?._seconds * 1000);
      return now.getTime() - itemTimestamp.getTime() < thresholdMs;
    });

    // Compare with previous data to find truly new issues
    const newIssuesFound = recentIssues.filter(
      (newItem) =>
        !previousDataRef.current.some((prevItem) => prevItem.id === newItem.id)
    );

    if (newIssuesFound.length > 0) {
      const notifications = newIssuesFound.map((issue) => ({
        id: issue.id,
        message: `New issue detected in ${collectionName}`,
        timestamp: new Date(),
        data: issue,
      }));

      setNewIssues((prev) => [...notifications, ...prev]);
      setHasNewIssues(true);

      // Show browser notification if enabled
      if (
        enableNotifications &&
        "Notification" in window &&
        Notification.permission === "granted"
      ) {
        newIssuesFound.forEach((issue) => {
          new Notification(notificationTitle, {
            body: `New issue detected in ${collectionName}`,
            icon: "/favicon.ico", // You can customize this
            tag: issue.id, // Prevents duplicate notifications
          });
        });
      }

      // Auto-clear new issues indicator after 30 seconds
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
      }

      notificationTimeoutRef.current = setTimeout(() => {
        setHasNewIssues(false);
      }, 30000);
    }

    // Update previous data reference
    previousDataRef.current = data;
  }, [
    data,
    collectionName,
    thresholdMinutes,
    enableNotifications,
    notificationTitle,
  ]);

  // Request notification permission on mount
  useEffect(() => {
    if (
      enableNotifications &&
      "Notification" in window &&
      Notification.permission === "default"
    ) {
      Notification.requestPermission();
    }
  }, [enableNotifications]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
      }
    };
  }, []);

  const clearNewIssues = () => {
    setNewIssues([]);
    setHasNewIssues(false);
  };

  const markAsRead = (issueId: string) => {
    setNewIssues((prev) => prev.filter((issue) => issue.id !== issueId));
    if (newIssues.length === 1) {
      setHasNewIssues(false);
    }
  };

  return {
    newIssues,
    hasNewIssues,
    lastUpdated,
    clearNewIssues,
    markAsRead,
    totalNewIssues: newIssues.length,
  };
}

// Specialized hook for health metrics notifications
export function useHealthMetricsNotification(collectionName: string) {
  return useNewIssuesNotification({
    collectionName,
    thresholdMinutes: 10, // Health metrics are usually updated less frequently
    notificationTitle: "Health Metrics Updated",
  });
}

// Specialized hook for audit issues notifications
export function useAuditIssuesNotification(collectionName: string) {
  return useNewIssuesNotification({
    collectionName,
    thresholdMinutes: 5, // Audit issues can be added more frequently
    notificationTitle: "New Audit Issue Detected",
  });
}
